import { useState } from 'react';
import { DealStates, DevelopmentStages } from '~/constants';
import { usePortfolioContext } from '~/hooks';
import { Form, Popup, Icon } from 'semantic-ui-react';
import { Button } from '~/components';
import PortfolioSubmissionModal from './PortfolioSubmissionModals';
import { useSubmitPortfolio } from '~/mutations/portfolio/useSubmitPortfolio';
import PortfolioBidTypeCheckModal from './PortfolioSubmissionModals/PortfolioBidTypeCheckModal';

export type PortfolioModalFormTypes = 'STANDALONE_PROJECT' | 'PORTFOLIO_PRICE_CHECK' | 'SUBMIT_PORTFOLIO' | null;

const PortfolioSubmissionButtons = () => {
  const { projectBlobs, portfolioState } = usePortfolioContext();
  const [currentForm, setCurrentForm] = useState<PortfolioModalFormTypes>(null);
  const closeModal = () => setCurrentForm(null);

  const readyForSubmission = portfolioState?.earliestState === DealStates.RETURNED_TO_DRAFT || portfolioState?.earliestState === DealStates.READY_TO_SUBMIT;
  const hasErrors = portfolioState?.earliestState === DealStates.PROJECT_HAS_ERRORS;
  const isStandaloneProject = projectBlobs?.length === 1;

  //Portfolios can only be one pricing type
  //But with the distinction of project acquisition for operating projects, this needs to be taken a step further
  //At this time project acquisition IS NOT a new pricing type in the db, but is a special case of the EPC cost + dev fee pricing type
  //So we are adding an additional condition to check if the portfolio has any operating projects, and if so all of the projects should be operating projects
  const pricingTypeSet = new Set(
    projectBlobs.map(({ project }: any) => project.pricingType)
  );
  const hasAnyOperating = projectBlobs.some(
    ({ project }: any) => project.developmentStage === DevelopmentStages.OPERATING
  );
  const hasAllOperating = projectBlobs.every(
    ({ project }: any) => project.developmentStage === DevelopmentStages.OPERATING
  );
  const hasBidTypeConditions = pricingTypeSet?.size > 1 || (hasAnyOperating && !hasAllOperating);

  const { status: submitPortfolioStatus } = useSubmitPortfolio();

  const handleClick = () => {
    if (isStandaloneProject) {
      setCurrentForm('STANDALONE_PROJECT');
    } else if (hasBidTypeConditions) {
      setCurrentForm('PORTFOLIO_PRICE_CHECK');
    } else {
      setCurrentForm('SUBMIT_PORTFOLIO');
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--x-small)'}}>
      {hasErrors && (
        <Popup
          position="bottom center"
          trigger={<Icon name="info circle" size="large" style={{ color: 'var(--color-charcoal)' }} tabIndex={0} />}
          content={'One or more of the projects in your portfolio is incomplete. Once you’ve addressed any outstanding issues, you will be able to submit your portfolio from here.'}          
          inverted
          on={['hover', 'focus']}
        />
      )}
      {(hasErrors || readyForSubmission) && (
        <Form>
          <Button 
            primary  
            size="small"
            onClick={() => handleClick()}
            disabled={hasErrors || submitPortfolioStatus === 'loading'}
          >
            {readyForSubmission ? 'Submit portfolio' :'Projects are incomplete'} 
          </Button>
        </Form>
      )}
    
      {(currentForm === 'STANDALONE_PROJECT' || currentForm === 'SUBMIT_PORTFOLIO') && (
        <PortfolioSubmissionModal 
          closeModal={closeModal} 
          currentForm={currentForm}
        />
      )}
      {currentForm === 'PORTFOLIO_PRICE_CHECK' && (
        <PortfolioBidTypeCheckModal
          closeModal={closeModal}
        />
      )}
    </div>
  );
};

export default PortfolioSubmissionButtons;